import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <div className="mb-8" data-testid="policy-section">
    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">{title}</h2>
    <div className="space-y-3 text-slate-600 leading-relaxed text-base">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-policy-page" className="bg-white min-h-screen">
      {/* Top Bar */}
      <header className="bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="policy-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_brandmitra-digital/artifacts/u617uzad_BM%20New%20logo%20png.png"
              alt="BrandMitraa Logo"
              className="h-16 w-auto bg-white rounded px-2 py-1"
            />
          </Link>
          <Link
            to="/"
            data-testid="policy-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-200 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <span className="inline-block text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Legal
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">Privacy Policy</h1>
          <p className="text-slate-500 mt-3 text-base">Last updated: June 2026</p>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-14 max-w-4xl">
        <div className="space-y-3 text-slate-600 leading-relaxed text-base mb-10">
          <p>
            At BrandMitra (accessible from{' '}
            <a
              href="https://brandmitra.in/"
              className="text-orange-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://brandmitra.in/
            </a>
            ), accessible, trusted, and transparent digital marketing service provision is our
            priority. This Privacy Policy outlines the types of information we collect, how we use
            it, and how we protect your personal data when you visit our website or use our
            services.
          </p>
          <p>
            By using our website, you hereby consent to our Privacy Policy and agree to its terms.
          </p>
        </div>

        <Section title="1. Information We Collect">
          <p>
            We collect information to provide better services to our clients and site visitors. The
            personal information you are asked to provide, and the reasons why you are asked to
            provide it, will be made clear at the point we ask for it.
          </p>
          <p className="font-semibold text-slate-800">Directly Provided Data:</p>
          <p>
            When you fill out a contact form, request a quote, subscribe to a newsletter, or
            communicate with us, we may collect:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Full Name</li>
            <li>Email Address</li>
            <li>Phone Number</li>
            <li>Business / Company Name</li>
            <li>Website URL and project details provided in your message</li>
          </ul>
          <p className="font-semibold text-slate-800">Automatically Collected Data (Log Files &amp; Cookies):</p>
          <p>
            Like most websites, BrandMitra follows standard procedures for using log files and
            cookies. Information collected includes:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Internet Protocol (IP) address</li>
            <li>Browser type and version</li>
            <li>Internet Service Provider (ISP)</li>
            <li>Date and time stamp</li>
            <li>Referring/exit pages and click behavior</li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect in various ways, including to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide, operate, and maintain our website and digital marketing services.</li>
            <li>Improve, personalize, and expand our website content and user experience.</li>
            <li>Understand and analyze how you interact with our website.</li>
            <li>
              Communicate with you regarding project inquiries, proposals, customer support, and
              marketing updates.
            </li>
            <li>
              Send you email updates, newsletters, or promotional communications (you may opt-out at
              any time).
            </li>
            <li>Prevent fraudulent activities and secure our online systems.</li>
          </ul>
        </Section>

        <Section title="3. Cookies and Analytics">
          <p>
            BrandMitra uses 'cookies' to store information including visitors' preferences and the
            pages on the website that the visitor accessed or visited.
          </p>
          <p>
            <span className="font-semibold text-slate-800">Analytics Tools:</span> We may use
            third-party analytics tools (such as Google Analytics) to track and analyze website
            traffic. These services use cookies to help us evaluate how users navigate the site.
          </p>
          <p>
            <span className="font-semibold text-slate-800">Managing Cookies:</span> You can choose
            to disable cookies through your individual browser options. Detailed information about
            cookie management with specific web browsers can be found on the browsers' respective
            websites.
          </p>
        </Section>

        <Section title="4. Third-Party Services and Advertising">
          <p>
            BrandMitra does not sell, trade, or rent your personal identification information to
            third parties.
          </p>
          <p>
            We may share generic aggregated demographic information not linked to any personal
            identification information with trusted partners and service providers (such as hosting
            providers, CRM software, or payment processors) strictly for the purpose of helping us
            operate our business and website.
          </p>
          <p>
            Third-party ad servers or ad networks use technologies like cookies, JavaScript, or Web
            Beacons that are used in their respective advertisements and links that appear on
            BrandMitra. They automatically receive your IP address when this occurs. Please note
            that BrandMitra has no access to or control over these cookies used by third-party
            advertisers.
          </p>
        </Section>

        <Section title="5. Data Protection Rights (Digital Personal Data Protection Act / GDPR)">
          <p>Depending on your location, you have certain rights regarding your personal data:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <span className="font-semibold text-slate-800">Right to Access:</span> You have the
              right to request copies of your personal data.
            </li>
            <li>
              <span className="font-semibold text-slate-800">Right to Rectification:</span> You have
              the right to request that we correct any information you believe is inaccurate or
              incomplete.
            </li>
            <li>
              <span className="font-semibold text-slate-800">Right to Erasure / Deletion:</span> You
              have the right to request that we erase your personal data under certain conditions.
            </li>
            <li>
              <span className="font-semibold text-slate-800">Right to Withdraw Consent:</span> Where
              processing is based on consent, you may withdraw your consent at any time.
            </li>
          </ul>
          <p>
            If you wish to exercise any of these rights, please contact us using the information
            provided below.
          </p>
        </Section>

        <Section title="6. Information Security">
          <p>
            We implement appropriate technical and organizational measures to safeguard your
            personal data against unauthorized access, alteration, disclosure, or destruction.
            However, please remember that no method of transmission over the internet or electronic
            storage is 100% secure.
          </p>
        </Section>

        <Section title="7. Children's Information">
          <p>
            BrandMitra does not knowingly collect any Personal Identifiable Information from children
            under the age of 13. If you think that your child provided this kind of information on
            our website, we strongly encourage you to contact us immediately, and we will make best
            efforts to promptly remove such information from our records.
          </p>
        </Section>

        <Section title="8. Changes to This Privacy Policy">
          <p>
            We may update our Privacy Policy from time to time. We advise you to review this page
            periodically for any changes. Changes are effective immediately after they are posted on
            this page.
          </p>
        </Section>

        <Section title="9. Contact Us">
          <p>
            If you have any questions, suggestions, or requests regarding this Privacy Policy, please
            do not hesitate to contact us:
          </p>
          <ul className="list-none space-y-1">
            <li>
              <span className="font-semibold text-slate-800">Company Name:</span> BrandMitra
            </li>
            <li>
              <span className="font-semibold text-slate-800">Website:</span>{' '}
              <a
                href="https://brandmitra.in/"
                className="text-orange-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://brandmitra.in/
              </a>
            </li>
          </ul>
        </Section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
